import './settings/settings-leaf.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { integrationsApi, type IntegrationAccount, type IntegrationCalendar } from '../api/endpoints.ts';
import { usePlanFeature } from './plan-gate.tsx';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextInput } from './ui.tsx';

/**
 * Settings → Your mailbox and calendars.
 *
 * The person's own connections, never a colleague's: a mailbox credential
 * belongs to whoever owns the mailbox, and there is no manager view of this
 * screen for the same reason there is no manager view of somebody's password.
 *
 * Two things this screen has to make plain, because getting either wrong is
 * how the feature does damage:
 *
 *   The iCloud field is an APP-SPECIFIC PASSWORD. Said in the label, in the
 *   help text, and in the placeholder, because somebody who types their Apple
 *   account password into a third-party app has been failed by the form.
 *
 *   A calendar can block scheduling without being visible. The two toggles are
 *   separate and the wording says what each does, because "why can nobody book
 *   me on Tuesday" is otherwise unanswerable from this page.
 */

const CONNECT_BASE = '/api/integrations/oauth';

function connectHref(provider: 'google' | 'microsoft', capability: 'email' | 'calendar', returnTo: string): string {
  return `${CONNECT_BASE}/${provider}/start?capability=${capability}&returnTo=${returnTo}`;
}

/** Wording for the codes the OAuth legs come back with. */
function connectErrorMessage(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case 'declined':
      return 'That connection was canceled, so nothing has changed.';
    case 'expired':
      return 'That took too long to finish. Try connecting again.';
    case 'not_enabled':
      return 'That provider is not available on this account.';
    case 'no_refresh_token':
      return 'The provider did not grant lasting access. Try connecting again and approve every permission it asks for.';
    case 'impersonated':
      return 'A support session cannot connect an account on your behalf. Sign in yourself to connect one.';
    case 'not_signed_in':
      return 'Your session ended before that finished. Sign in and try again.';
    default:
      return 'That connection could not be completed. Please try again.';
  }
}

/** `returnTo` is the in-app page the connect round trip comes back to. */
export default function IntegrationsSection({ returnTo = '/settings' }: { returnTo?: string }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.list(),
  });

  // The OAuth legs are full-page redirects, so their outcome arrives as a query
  // parameter rather than as a mutation result. Cleared once read, so a reload
  // does not replay a stale banner.
  const connected = searchParams.get('integration_connected');
  const connectError = connectErrorMessage(searchParams.get('integration_error'));

  function dismissRedirectNotice() {
    const next = new URLSearchParams(searchParams);
    for (const key of ['integration_connected', 'integration_error', 'integration_provider', 'capability']) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integrations'] });

  /**
   * The picker's value is an account id, not a provider.
   *
   * Somebody with two Gmail accounts connected would otherwise see two options
   * that were literally the same value: the preference stored "google", and
   * the router picked whichever it found first. Choosing the second mailbox
   * changed nothing, silently.
   *
   * The stored shape stays as it is — a provider preference plus the account
   * `automatic` should prefer — so this sends both: the id pins the mailbox
   * and the provider keeps working for anybody with one of each.
   */
  const setSender = useMutation({
    mutationFn: (value: string) => {
      if (value === 'automatic' || value === 'company') {
        return integrationsApi.setSender({ preference: value, primaryAccountId: null });
      }
      const account = data?.accounts.find((entry) => entry.id === value);
      return integrationsApi.setSender({
        preference: account?.provider ?? 'automatic',
        primaryAccountId: account?.id ?? null,
      });
    },
    onSuccess: async () => {
      setNotice({ tone: 'success', text: 'Saved.' });
      await invalidate();
    },
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => integrationsApi.disconnect(id),
    onSuccess: async (result) => {
      setNotice({
        tone: 'success',
        text: result.revokeAtProvider
          ? // Apple keeps its own copy of the app-specific password and this app
            // cannot revoke it remotely. Saying so is the difference between
            // somebody cleaning up and somebody leaving a live credential in
            // their Apple Account for years.
            'Disconnected. You can also delete the app-specific password from your Apple Account.'
          : 'Disconnected.',
      });
      await invalidate();
    },
  });

  if (integrations.isLoading) return <LoadingBlock label="Loading your connected accounts" />;
  const data = integrations.data;
  if (!data) return null;

  const canConnectGoogle = data.available.google && data.policy.google;
  const canConnectMicrosoft = data.available.microsoft && data.policy.microsoft;
  const canConnectICloud = data.available.icloud && data.policy.icloud;
  // Mail and calendars part company at the plan: sending customer email from
  // your own address is part of quoting, which the Free plan does; a connected
  // calendar belongs to a schedule it does not have. So the calendar buttons
  // go rather than the whole card — the Worker refuses those connections
  // anyway (routes/integrations-oauth.ts).
  const canConnectCalendar = usePlanFeature('scheduling');
  // Mail is only offered where the company allows employee sending at all,
  // as for iCloud below: with it off, the Worker refuses the mail connection
  // (routes/integrations-oauth.ts), so the button could only end in an error.
  const canConnectMail = data.policy.employeeEmailSending;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="integrations-heading">
      <div className="work-section-heading">
        <h2 id="integrations-heading" className="mb-2 text-lg font-semibold">
          Your mailbox and calendars
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        Send customer email from your own address, and let the calendar you already keep decide when you are free. These
        are yours — nobody else on the team can see or use them.
      </p>

      {connected ? (
        <div className="mb-4">
          <Alert tone="success">
            Connected.{' '}
            <button type="button" className="underline" onClick={dismissRedirectNotice}>
              Dismiss
            </button>
          </Alert>
        </div>
      ) : null}

      {connectError ? (
        <div className="mb-4">
          <Alert tone="error">
            {connectError}{' '}
            <button type="button" className="underline" onClick={dismissRedirectNotice}>
              Dismiss
            </button>
          </Alert>
        </div>
      ) : null}

      {notice ? (
        <div className="mb-4">
          <Alert tone={notice.tone}>{notice.text}</Alert>
        </div>
      ) : null}

      {!data.policy.employeeEmailSending ? (
        <div className="mb-4">
          <Alert tone="info">
            Your company sends all customer email from its own address. You can still connect a calendar.
          </Alert>
        </div>
      ) : null}

      {data.accounts.length > 0 ? (
        <ul className="mb-6 flex flex-col gap-4">
          {data.accounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              twoWayAllowed={data.policy.twoWayCalendarSync}
              onChanged={invalidate}
              onDisconnect={() => disconnect.mutate(account.id)}
              onNotice={setNotice}
            />
          ))}
        </ul>
      ) : null}

      <h3 className="mb-2 text-sm font-semibold">Connect an account</h3>
      <div className="mb-6 flex flex-wrap gap-2">
        {canConnectGoogle ? (
          <>
            {canConnectMail ? (
              <a className="btn btn-secondary" href={connectHref('google', 'email', returnTo)}>
                Connect Gmail
              </a>
            ) : null}
            {canConnectCalendar ? (
              <a className="btn btn-secondary" href={connectHref('google', 'calendar', returnTo)}>
                Connect Google Calendar
              </a>
            ) : null}
          </>
        ) : null}
        {canConnectMicrosoft ? (
          <>
            {canConnectMail ? (
              <a className="btn btn-secondary" href={connectHref('microsoft', 'email', returnTo)}>
                Connect Microsoft 365 mail
              </a>
            ) : null}
            {canConnectCalendar ? (
              <a className="btn btn-secondary" href={connectHref('microsoft', 'calendar', returnTo)}>
                Connect Outlook Calendar
              </a>
            ) : null}
          </>
        ) : null}
      </div>
      {/* Two buttons per provider rather than one, which is the whole point of
          asking for permissions when a feature is switched on: signing in
          should not put a "send email as you" consent screen in front of
          somebody who only wanted to log in. */}

      {canConnectICloud ? (
        <ICloudConnectForm
          calendarAllowed={canConnectCalendar}
          emailSendingAllowed={data.policy.employeeEmailSending}
          onConnected={invalidate}
          onNotice={setNotice}
        />
      ) : null}

      {data.policy.employeeEmailSending && data.accounts.some((account) => account.emailCapability === 'connected') ? (
        <div className="mt-6 max-w-sm">
          <Select
            label="Send customer email as"
            // The id when one mailbox is pinned, otherwise the mode. Reading it
            // back this way keeps the control showing what is actually stored
            // rather than the first account of a matching provider.
            value={
              data.primaryAccountId &&
              data.preferredEmailSender !== 'automatic' &&
              data.preferredEmailSender !== 'company'
                ? data.primaryAccountId
                : data.preferredEmailSender
            }
            onChange={(event) => setSender.mutate(event.target.value)}
            disabled={setSender.isPending}
            options={[
              { value: 'automatic', label: 'Automatic' },
              ...data.accounts
                .filter((account) => account.emailCapability === 'connected')
                .map((account) => ({
                  value: account.id,
                  label: `${account.providerLabel} — ${account.email}`,
                })),
              { value: 'company', label: 'Company email' },
            ]}
          />
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">
            If your mailbox cannot be reached, the message goes out over the company address with your address as the
            reply-to, so a customer&rsquo;s reply still reaches you.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function AccountCard({
  account,
  twoWayAllowed,
  onChanged,
  onDisconnect,
  onNotice,
}: {
  account: IntegrationAccount;
  twoWayAllowed: boolean;
  onChanged: () => Promise<unknown>;
  onDisconnect: () => void;
  onNotice: (notice: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const toggle = useMutation({
    mutationFn: (body: { emailEnabled?: boolean; calendarEnabled?: boolean }) =>
      integrationsApi.update(account.id, body),
    onSuccess: () => void onChanged(),
  });

  const test = useMutation({
    mutationFn: () => integrationsApi.test(account.id),
    onSuccess: (result) => {
      onNotice(
        result.status === 'connected'
          ? { tone: 'success', text: 'That connection is working.' }
          : {
              tone: 'error',
              // Which half failed, because an account can hold both and they
              // fail independently. The reason stays a category and never the
              // server's reply — a "535 5.7.8" is true and useless advice.
              text: result.failedCapabilities?.includes('calendar')
                ? result.failedCapabilities.includes('email')
                  ? 'We could not reach the mailbox or the calendars. Generate a new Apple app-specific password and reconnect.'
                  : 'The mailbox is fine, but we could not reach the calendars. Generate a new Apple app-specific password and reconnect.'
                : 'We could not sign in. Generate a new Apple app-specific password and reconnect.',
            },
      );
      void onChanged();
    },
  });

  const needsAttention = account.status !== 'active';

  return (
    <li className="rounded border border-[color:var(--color-line)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{account.providerLabel}</p>
          <p className="text-sm text-[color:var(--color-muted)]">{account.email}</p>
        </div>
        <div className="flex gap-2">
          {account.provider === 'icloud' ? (
            <Button variant="secondary" onClick={() => test.mutate()} busy={test.isPending}>
              Test
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onDisconnect}>
            Disconnect
          </Button>
        </div>
      </div>

      {needsAttention ? (
        <div className="mt-3">
          <Alert tone="error">{account.lastError ?? 'This connection needs attention. Reconnect it.'}</Alert>
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-2">
        {account.emailCapability === 'connected' ? (
          <Checkbox
            checked={account.emailEnabled}
            onChange={(event) => toggle.mutate({ emailEnabled: event.target.checked })}
            label={`Send customer email as ${account.email}`}
          />
        ) : null}
        {account.calendarCapability === 'connected' ? (
          <Checkbox
            checked={account.calendarEnabled}
            onChange={(event) => toggle.mutate({ calendarEnabled: event.target.checked })}
            label="Use this account's calendars"
          />
        ) : null}
      </div>

      {/* Health, as the two facts somebody can actually read something into.
          "Connected" on its own tells you nothing about whether it still works. */}
      <p className="mt-3 text-xs text-[color:var(--color-muted)]">
        {account.lastEmailAt ? `Last email ${new Date(account.lastEmailAt).toLocaleString()}. ` : null}
        {account.lastCalendarSyncAt
          ? `Calendars last checked ${new Date(account.lastCalendarSyncAt).toLocaleString()}.`
          : null}
      </p>

      {account.calendars.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-3 border-t border-[color:var(--color-line)] pt-3">
          {account.calendars.map((calendar) => (
            <CalendarRow key={calendar.id} calendar={calendar} twoWayAllowed={twoWayAllowed} onChanged={onChanged} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function CalendarRow({
  calendar,
  twoWayAllowed,
  onChanged,
}: {
  calendar: IntegrationCalendar;
  twoWayAllowed: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const update = useMutation({
    mutationFn: (body: Parameters<typeof integrationsApi.updateCalendar>[1]) =>
      integrationsApi.updateCalendar(calendar.id, body),
    onSuccess: () => void onChanged(),
  });

  const retry = useMutation({
    mutationFn: () => integrationsApi.retryCalendar(calendar.id),
    onSuccess: () => void onChanged(),
  });

  return (
    <li>
      <p className="text-sm font-medium">{calendar.name}</p>
      <div className="mt-1 flex flex-col gap-1">
        <Checkbox
          checked={calendar.availabilityEnabled}
          onChange={(event) => update.mutate({ availabilityEnabled: event.target.checked })}
          label="Block scheduling when I am busy on this calendar"
        />
        <Checkbox
          checked={calendar.displayEnabled}
          onChange={(event) => update.mutate({ displayEnabled: event.target.checked })}
          label="Show these events in the schedule"
        />
        {/* Not offered at all for a read-only share — a subscribed feed or a
            colleague's calendar. Ticking it there would queue visits the
            provider refuses on every sync tick, which reads as the integration
            being broken rather than as the calendar being unwritable. */}
        {calendar.providerCanWrite ? (
          <Checkbox
            checked={calendar.writeAppointmentsEnabled}
            onChange={(event) => update.mutate({ writeAppointmentsEnabled: event.target.checked })}
            label="Add my assigned visits to this calendar"
          />
        ) : (
          <p className="text-xs text-[color:var(--color-muted)]">
            This calendar is read-only, so your visits cannot be added to it.
          </p>
        )}
        {/* Only where the visits are being written at all — and only where the
            company has switched two-way sync on, because letting an outside
            calendar move a customer's booking is an owner's decision, not an
            employee's. */}
        {calendar.writeAppointmentsEnabled && twoWayAllowed ? (
          <Checkbox
            checked={calendar.syncDirection === 'two_way'}
            onChange={(event) => update.mutate({ syncDirection: event.target.checked ? 'two_way' : 'app_to_calendar' })}
            label="Moving a visit in this calendar moves it in the schedule"
          />
        ) : null}
      </div>
      {/* Parked visits, said out loud. The sync pass deliberately does not
          claim a conflicted link, so without this the promise that something
          was "left for review" is a promise to nobody. */}
      {calendar.conflictCount > 0 ? (
        <div className="mt-2">
          <Alert tone="warning">
            {calendar.conflictCount === 1
              ? 'One visit was changed on this calendar in a way we did not act on.'
              : `${calendar.conflictCount} visits were changed on this calendar in ways we did not act on.`}{' '}
            <button type="button" className="underline" disabled={retry.isPending} onClick={() => retry.mutate()}>
              Try them again
            </button>
          </Alert>
        </div>
      ) : null}

      {/* The sentence the whole design turns on. Somebody who does not read it
          will eventually ask why a calendar they cannot see is stopping
          bookings, and this is the answer, on the screen where they set it. */}
      <p className="mt-1 text-xs text-[color:var(--color-muted)]">
        Blocking and showing are separate. A calendar can stop the office booking over you without anyone seeing what is
        on it — they only see that you are busy.
      </p>
    </li>
  );
}

/**
 * Connecting iCloud — mail, calendar, or both.
 *
 * One form because Apple issues one app-specific password and it authenticates
 * both SMTP and CalDAV; two checkboxes because they are two separate grants.
 * Somebody who wants the office to stop booking over their dentist appointments
 * has not thereby agreed that customer mail should leave from their personal
 * Apple address, and the form should not decide that for them.
 *
 * The word "app-specific" appears in the label, the help text and the
 * placeholder. That is not redundancy: somebody who types their Apple account
 * password into a third-party form has been failed by the form, and Apple
 * would refuse it anyway — so the failure would be confusing as well as
 * avoidable.
 */
function ICloudConnectForm({
  calendarAllowed,
  emailSendingAllowed,
  onConnected,
  onNotice,
}: {
  /** False on a plan with no schedule: the calendar half is not offered. */
  calendarAllowed: boolean;
  emailSendingAllowed: boolean;
  onConnected: () => Promise<unknown>;
  onNotice: (notice: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [appSpecificPassword, setAppSpecificPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Mail is only offered where the company allows employee sending at all, so
  // the calendar is what a form with the mail half unavailable is for.
  const [wantMail, setWantMail] = useState(emailSendingAllowed);
  const [wantCalendar, setWantCalendar] = useState(calendarAllowed && !emailSendingAllowed);

  const connect = useMutation({
    mutationFn: async () => {
      const connected: string[] = [];
      // Sequential, and mail first where both are asked for: each call proves
      // the credential against the protocol it is for, and a calendar that
      // connects while SMTP refuses is a real outcome worth reporting as one.
      if (wantMail && emailSendingAllowed) {
        await integrationsApi.connectICloudMail({ email, appSpecificPassword });
        connected.push('Mail');
      }
      if (wantCalendar) {
        await integrationsApi.connectICloudCalendar({ email, appSpecificPassword });
        connected.push('Calendar');
      }
      return connected;
    },
    onSuccess: async (connected) => {
      setEmail('');
      // Cleared immediately on success as well as on failure: the password has
      // been stored encrypted server-side and has no reason to sit in a form
      // field afterwards.
      setAppSpecificPassword('');
      setFieldErrors({});
      onNotice({ tone: 'success', text: `iCloud ${connected.join(' and ')} connected.` });
      await onConnected();
    },
    onError: async (error) => {
      setAppSpecificPassword('');
      if (error instanceof ApiRequestError && error.fields) setFieldErrors(error.fields);
      onNotice({
        tone: 'error',
        text:
          error instanceof ApiRequestError
            ? error.message
            : 'We could not connect that account. Check the address and password and try again.',
      });
      // One half may already have succeeded before the other failed, and the
      // list has to show that rather than leaving somebody to guess.
      await onConnected();
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    connect.mutate();
  }

  const nothingChosen = !(wantCalendar && calendarAllowed) && !(wantMail && emailSendingAllowed);

  return (
    <form className="max-w-md rounded border border-[color:var(--color-line)] p-4" onSubmit={handleSubmit}>
      <h3 className="mb-1 text-sm font-semibold">Connect iCloud</h3>
      <p className="mb-3 text-xs text-[color:var(--color-muted)]">
        Sign in to your Apple Account, open <strong>Sign-In and Security</strong>, choose{' '}
        <strong>App-Specific Passwords</strong>, generate one, and paste it below.{' '}
        <strong>Do not enter your normal Apple password.</strong>
      </p>

      <TextInput
        label="iCloud email"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="you@icloud.com"
        autoComplete="off"
        error={fieldErrors.email}
      />

      <TextInput
        label="App-Specific Password"
        hint="Sixteen letters, shown by Apple as four groups of four."
        type="password"
        value={appSpecificPassword}
        onChange={(event) => setAppSpecificPassword(event.target.value)}
        placeholder="xxxx-xxxx-xxxx-xxxx"
        // Never offered to a password manager as an account password, and
        // never restored into the field on a back navigation.
        autoComplete="off"
        error={fieldErrors.appSpecificPassword}
      />

      <div className="mb-3 flex flex-col gap-1">
        {emailSendingAllowed ? (
          <Checkbox
            checked={wantMail}
            onChange={(event) => setWantMail(event.target.checked)}
            label="Send customer email from this address"
          />
        ) : null}
        {calendarAllowed ? (
          <Checkbox
            checked={wantCalendar}
            onChange={(event) => setWantCalendar(event.target.checked)}
            label="Use my iCloud calendars"
          />
        ) : null}
      </div>

      <Button type="submit" variant="primary" busy={connect.isPending} disabled={nothingChosen}>
        Connect
      </Button>
      <p className="mt-2 text-xs text-[color:var(--color-muted)]">
        We check the connection with Apple before saving it, and never send a test message to anyone. A connected
        calendar starts with booking blocks and display turned off. Choose each separately below. Busy events block
        bookings only when you enable that calendar and the owner enables external-calendar blocking for the company.
      </p>
    </form>
  );
}
